import { useRef, useState, useEffect } from "react";
import { IoClose, IoArrowBackOutline } from "react-icons/io5";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

// Components
import CameraPreview from "./createVideo/components/CameraPreview";
import CameraControls from "./createVideo/components/CameraControls";
import RecordingControls from "./createVideo/components/RecordingControls";
import EditingTools from "./createVideo/components/EditingTools";
import EditMenuTools from "./createVideo/components/EditMenuTools";
import TextStyleTools from "./createVideo/components/TextStyleTools";
import TextDurationEditor from "./createVideo/components/TextDurationEditor";
import VideoForm from "./createVideo/components/VideoForm";
import ExitPopup from "./createVideo/components/ExitPopup";
import CompletionPopup from "./createVideo/components/CompletionPopup";
import DraftPopup from "./createVideo/components/DraftPopup";
// import AudioIndicator from "./createVideo/components/AudioIndicator";

// Hooks
import useCamera from "./createVideo/hooks/useCamera";
import useRecording from "./createVideo/hooks/useRecording";
import useTimer from "./createVideo/hooks/useTimer";
import { motion, AnimatePresence } from "framer-motion";

// Redux
import { uploadVideoToS3, uploadVideoData, addPendingUpload } from "../../redux/ApiSlice/UploadVideoSlice";
import { createDraft } from "../../redux/ApiSlice/DraftSlice";
import { postCreatedUpdateState } from "../../redux/ApiSlice/AccountProfileSlice";
import { getPrivateHas } from "../../redux/ApiSlice/AccountPrivateSlice";
import { AlertTriangle, FolderLock } from "lucide-react";

const CreateVideos = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { User_profile } = useSelector((state) => state.AccountProfile);
    const reduxUploadProgress = useSelector((state) => state.Uploadvideo.progress);
    const { hasPin } = useSelector((state) => state.AccountPrivate);

    const [step, setStep] = useState(1);
    const [showExitPopup, setShowExitPopup] = useState(false);
    const [selectedTimer, setSelectedTimer] = useState("00:15");
    const [showDraftPopup, setShowDraftPopup] = useState(false);
    const [description, setDescription] = useState("");
    const [selectedCategory, setSelectedCategory] = useState("");
    const [isPrivate, setIsPrivate] = useState(false);
    const [allowComments, setAllowComments] = useState(true);
    const [allowDownload, setAllowDownload] = useState(true);
    const [isEditMode, setIsEditMode] = useState(false);
    const [isTextMode, setIsTextMode] = useState(false);
    const [textInput, setTextInput] = useState("");
    const [selectedTextStyle, setSelectedTextStyle] = useState(0);
    const [textStartTime, setTextStartTime] = useState(0);
    const [textEndTime, setTextEndTime] = useState(5);
    const [textOverlays, setTextOverlays] = useState([]);
    const [showTextDurationEditor, setShowTextDurationEditor] = useState(false);
    const [pauseMarkers, setPauseMarkers] = useState([]);
    const [showFormatAlert, setShowFormatAlert] = useState(false);
    const [isSavingDraft, setIsSavingDraft] = useState(false);
    const [selectedFilter, setSelectedFilter] = useState("none");
    const [showFilterSheet, setShowFilterSheet] = useState(false);

    // Upload state
    const [isUploadingToS3, setIsUploadingToS3] = useState(false);
    const [isPosting, setIsPosting] = useState(false);
    const [uploadError, setUploadError] = useState(null);
    const [showPrivatePopup, setShowPrivatePopup] = useState(false);
    const uploadTaskRef = useRef(null);

    const [customThumbnail, setCustomThumbnail] = useState(null);
    const [customThumbnailPreview, setCustomThumbnailPreview] = useState(null);
    const thumbnailInputRef = useRef(null);

    const videoRef = useRef(null);

    // Custom hooks
    const { cameraStream, cameraPermission, requestCameraAccess, switchCamera } = useCamera();
    const {
        mediaRecorder,
        recordedVideoUrl,
        isRecording,
        isPaused,
        startRecording,
        pauseRecording,
        setRecordedVideoUrl,
        resetRecording,
    } = useRecording(cameraStream);
    const {
        elapsedTime,
        showCompletionPopup,
        setShowCompletionPopup,
        formatTime,
        getProgress,
        getProgressStyle,
        resetTimer,
        setElapsedTime
    } = useTimer(selectedTimer, isRecording, mediaRecorder);

    // Fetch private folder status on mount
    useEffect(() => {
        dispatch(getPrivateHas());
    }, [dispatch]);

    // Cleanup camera stream when component unmounts
    useEffect(() => {
        return () => {
            // Stop all camera tracks when leaving the page
            if (cameraStream) {
                cameraStream.getTracks().forEach(track => {
                    track.stop();
                });
            }
        };
    }, [cameraStream]);

    // Event handlers
    const handleCloseClick = () => {
        setShowExitPopup(true);
    };

    const handlePauseRecording = () => {
        // Add current elapsed time as a pause marker
        setPauseMarkers(prev => [...prev, elapsedTime]);
        pauseRecording();
    };

    const handleSaveToDraft = () => {
        setShowExitPopup(false);
        setShowDraftPopup(true);
    };

    const handleCancel = () => {
        setShowExitPopup(false);
    };

    const handleExitWithoutSaving = () => {
        // Stop camera before navigating
        if (cameraStream) {
            cameraStream.getTracks().forEach(track => {
                track.stop();
            });
        }

        setShowExitPopup(false);
        navigate("/");
    };

    const handleTimerSelect = (time) => {
        setSelectedTimer(time);
        // User will need to manually click record button to continue
    };

    const handleDraftClick = () => {
        navigate("/draft-videos");
    };

    const handleCancelUpload = () => {
        if (uploadTaskRef.current?.abort) {
            uploadTaskRef.current.abort();
        }
        setIsUploadingToS3(false);
        setIsPosting(false);
        setUploadError(null);
    };

    const handleThumbnailChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        if (customThumbnailPreview) URL.revokeObjectURL(customThumbnailPreview);
        setCustomThumbnail(file);
        setCustomThumbnailPreview(URL.createObjectURL(file));
    };

    const handleRemoveThumbnail = () => {
        if (customThumbnailPreview) URL.revokeObjectURL(customThumbnailPreview);
        setCustomThumbnail(null);
        setCustomThumbnailPreview(null);
        if (thumbnailInputRef.current) thumbnailInputRef.current.value = "";
    };

    const handlePrivateToggle = () => {
        if (!hasPin && !isPrivate) {
            setShowPrivatePopup(true);
            return;
        }
        setIsPrivate((prev) => !prev);
    };

    const handlePostVideo = async () => {
        if (!recordedVideoUrl) {
            alert("No recorded video found.");
            return;
        }

        setUploadError(null);
        setIsUploadingToS3(true);

        try {
            // Convert blob URL → File
            const response = await fetch(recordedVideoUrl);
            const blob = await response.blob();
            const file = new File([blob], `create-video-${Date.now()}.webm`, {
                type: blob.type || "video/webm",
            });

            // S3 multipart upload — progress tracked via Redux (reduxUploadProgress selector)
            const uploadTask = dispatch(uploadVideoToS3({ file }));
            uploadTaskRef.current = uploadTask;
            const uploadResult = await uploadTask;

            if (!uploadVideoToS3.fulfilled.match(uploadResult)) {
                throw new Error(uploadResult?.payload || "S3 upload failed");
            }

            const s3Key = uploadResult.payload?.s3_key;
            if (!s3Key) throw new Error("Missing s3_key after upload");

            setIsUploadingToS3(false);
            setIsPosting(true);

            const durationMins = Math.floor(elapsedTime / 60);
            const durationSecs = Math.floor(elapsedTime % 60);
            const postData = {
                body: description,
                s3_key: s3Key,
                interest: selectedCategory,
                is_private: isPrivate,
                comments_enabled: allowComments,
                download_enabled: allowDownload,
                user_id: User_profile?.id,
                thumbnail: customThumbnail,
                is_created_video: true,
                duration: `${durationMins}.${durationSecs.toString().padStart(2, '0')}`,
            };

            const createdPost = await dispatch(uploadVideoData(postData)).unwrap();
            await dispatch(postCreatedUpdateState(createdPost));
            dispatch(addPendingUpload(createdPost));
            navigate("/account");
        } catch (err) {
            if (err?.message === "Upload aborted" || String(err).includes("aborted")) return;
            console.error("[CreateVideos] Post failed", err);
            setUploadError(err?.message || "Upload failed. Please try again.");
        } finally {
            setIsUploadingToS3(false);
            setIsPosting(false);
            uploadTaskRef.current = null;
        }
    };

    const handleDraftConfirm = () => {
        setShowDraftPopup(false);
    };

    const handleDraftCancel = () => {
        setShowDraftPopup(false);
    };

    const handleEditClick = () => {
        setIsEditMode(true);
    };

    const handleTextClick = () => {
        setIsTextMode(true);
    };

    const handleShowTextDurationEditor = () => {
        setShowTextDurationEditor(true);
    };

    const handleCloseTextDurationEditor = () => {
        setShowTextDurationEditor(false);
    };

    const handleEditToolSelect = (toolId) => {
        // Handle specific edit tool functionality here
    };

    // Recording completion handlers
    const handleContinue = () => {
        setShowCompletionPopup(false);
        setStep(2);
    };

    const handleSaveToStorageDraft = async () => {
        if (!recordedVideoUrl || isSavingDraft) return;
        setIsSavingDraft(true);
        try {
            const response = await fetch(recordedVideoUrl);
            const blob = await response.blob();
            const file = new File([blob], `draft-video-${Date.now()}.webm`, {
                type: blob.type || "video/webm",
            });

            // S3 multipart upload (initiate/complete) — same flow as handlePostVideo
            const uploadTask = dispatch(uploadVideoToS3({ file }));
            uploadTaskRef.current = uploadTask;
            const uploadResult = await uploadTask;

            if (!uploadVideoToS3.fulfilled.match(uploadResult)) {
                throw new Error(uploadResult?.payload || "S3 upload failed");
            }

            const s3Key = uploadResult.payload?.s3_key;
            if (!s3Key) throw new Error("Missing s3_key after upload");

            await dispatch(createDraft({
                s3_key: s3Key,
                interest: selectedCategory,
                body: description,
                is_private: isPrivate,
                comments_enabled: allowComments,
                download_enabled: allowDownload,
            })).unwrap();
            setShowCompletionPopup(false);
            handleDraftClick();
        } catch (err) {
            if (err?.message === "Upload aborted" || String(err).includes("aborted")) return;
            console.error("[CreateVideos] Save draft failed", err);
        } finally {
            setIsSavingDraft(false);
            uploadTaskRef.current = null;
        }
    };

    const handleDeleteVideo = () => {
        if (recordedVideoUrl) URL.revokeObjectURL(recordedVideoUrl);
        resetRecording();
        setPauseMarkers([]);
        resetTimer();
    };

    // Helper to format timer for display (abbreviated)
    const formatTimerForDisplay = (timerString) => {
        const [minutes, seconds] = timerString.split(':').map(Number);
        if (minutes === 0) {
            return `${seconds}s`;
        } else if (seconds === 0) {
            return `${minutes}m`;
        } else {
            return `${minutes}m ${seconds}s`;
        }
    };

    // Helper to calculate remaining time
    const getRemainingTime = () => {
        const [minutes, seconds] = selectedTimer.split(':').map(Number);
        const totalSeconds = minutes * 60 + seconds;
        const remaining = totalSeconds - elapsedTime;
        return remaining > 0 ? remaining : 0;
    };

    const timerOptions = [
        { label: "15s", value: "00:15" },
        { label: "30s", value: "00:30" },
        { label: "45s", value: "00:45" },
        { label: "1m", value: "01:00" },
        { label: "2m", value: "02:00" },
        { label: "3m", value: "03:00" },
    ];

    return (
        <div className="h-full bg-white dark:bg-[#1E1D21] relative">
            <input
                type="file"
                accept="image/*"
                ref={thumbnailInputRef}
                className="hidden"
                onChange={handleThumbnailChange}
            />
            {/* Top Bar */}
            <div className={`absolute w-full h-14 p-2 top-0 left-0 flex items-center justify-between  z-10 ${step === 3 ? "bg-[#BDE6FF] rounded-b-xl h-12" : "bg-opacity-40 bg-black dark:bg-[#1E1D21]"}`}>
                {step === 1 && (
                    <IoClose
                        className="text-[22px] text-white cursor-pointer"
                        onClick={handleCloseClick}
                    />
                )}
                {step >= 2 && (
                    <IoArrowBackOutline
                        onClick={() => {
                            if (isTextMode) {
                                setIsTextMode(false);
                            } else if (isEditMode) {
                                setIsEditMode(false);
                            } else {
                                // When going back to step 1, restart the camera stream
                                if (step === 2) {
                                    requestCameraAccess();
                                }
                                setStep(step - 1);
                            }
                        }}
                        className={`text-[22px] ${step === 3 ? "text-black" : "text-white"} cursor-pointer`}
                    />
                )}
                {/* Timer display in middle */}
                {step === 1 && selectedTimer !== "00:00" && (
                    <div className="absolute left-1/2 transform -translate-x-1/2">
                        <p className="text-white text-[16px] font-medium">
                            {(isRecording || (isPaused && elapsedTime > 0)) ? formatTime(getRemainingTime()) : selectedTimer}
                        </p>
                    </div>
                )}

                {/* Audio Indicator - Shows when recording */}
                {/* {step === 1 && isRecording && !showCompletionPopup && getRemainingTime() !== 0 && (
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                        <AudioIndicator
                            cameraStream={cameraStream}
                            isRecording={isRecording}
                        />
                    </div>
                )} */}

                {/* Next button when paused */}
                {step === 1 && isPaused && elapsedTime > 0 && (
                    <button
                        onClick={() => {
                            // Stop camera stream when moving to step 2
                            if (cameraStream) {
                                cameraStream.getTracks().forEach(track => {
                                    track.stop();
                                });
                            }
                            setStep(2);
                        }}
                        className="mr-1 w-20 text-[14px] rounded font-bold bg-[#BDE6FF] text-black py-[3px]"
                    >
                        Next
                    </button>
                )}

                {step === 2 && !isEditMode && !isTextMode && (
                    <button
                        onClick={() => setStep(step + 1)}
                        className="mr-1 w-20 text-[14px] rounded font-bold bg-[#BDE6FF] text-black py-[3px]"
                    >
                        Next
                    </button>
                )}

                {step === 2 && isEditMode && (
                    <button
                        onClick={() => setIsEditMode(false)}
                        className="mr-1 w-20 text-[14px] rounded font-bold bg-[#BDE6FF] text-black py-[3px]"
                    >
                        Done
                    </button>
                )}

                {step === 2 && isTextMode && (
                    <p
                        onClick={() => setIsTextMode(false)}
                        className="mr-1 text-[16px] text-white font-medium cursor-pointer"
                    >
                        Done
                    </p>
                )}

                {step === 3 && (
                    <button
                        onClick={() => setStep(2)}
                        className="mr-28 w-20 text-[18px] font-bold text-black"
                    >
                        Preview
                    </button>
                )}
            </div>

            {/* Camera Preview */}
            <div className="w-full h-full flex items-start justify-center">
                <CameraPreview
                    step={step}
                    cameraStream={cameraStream}
                    cameraPermission={cameraPermission}
                    recordedVideoUrl={recordedVideoUrl}
                    requestCameraAccess={requestCameraAccess}
                    isEditMode={isEditMode}
                    isTextMode={isTextMode}
                    textInput={textInput}
                    selectedTextStyle={selectedTextStyle}
                    textOverlays={textOverlays}
                    setTextInput={setTextInput}
                    videoRef={videoRef}
                    customThumbnailPreview={customThumbnailPreview}
                    thumbnailInputRef={thumbnailInputRef}
                    onRemoveThumbnail={handleRemoveThumbnail}
                    selectedFilter={selectedFilter}
                />
            </div>

            {/* Camera Controls (Step 1 only) */}
            {step === 1 && <CameraControls onSwitchCamera={switchCamera} cameraStream={cameraStream} />}

            {/* Recording Controls (Step 1 only) */}
            {step === 1 && (
                <RecordingControls
                    isRecording={isRecording}
                    isPaused={isPaused}
                    startRecording={startRecording}
                    pauseRecording={handlePauseRecording}
                    handleDraftClick={handleDraftClick}
                    selectedTimer={selectedTimer}
                    onTimerSelect={handleTimerSelect}
                    timerOptions={timerOptions}
                    formatTimerForDisplay={formatTimerForDisplay}
                    elapsedTime={elapsedTime}
                    formatTime={formatTime}
                    getProgress={getProgress}
                    getProgressStyle={getProgressStyle}
                    pauseMarkers={pauseMarkers}
                    showFormatAlert={showFormatAlert}
                    setShowFormatAlert={setShowFormatAlert}
                    onOpenFilterSheet={() => setShowFilterSheet(true)}
                />
            )}

            {/* Editing Tools (Step 2 only) */}
            {step === 2 && !isEditMode && !isTextMode && (
                <EditingTools
                    recordedVideoUrl={recordedVideoUrl}
                    elapsedTime={elapsedTime}
                    onEditClick={handleEditClick}
                    onTextClick={handleTextClick}
                    videoRef={videoRef}
                />
            )}

            {/* Edit Menu Tools (Step 2 in edit mode only) */}
            {step === 2 && isEditMode && (
                <EditMenuTools
                    onToolSelect={handleEditToolSelect}
                />
            )}

            {/* Text Style Tools (Step 2 in text mode only) */}
            {step === 2 && isTextMode && (
                <TextStyleTools
                    selectedTextStyle={selectedTextStyle}
                    setSelectedTextStyle={setSelectedTextStyle}
                    onShowDurationEditor={handleShowTextDurationEditor}
                    elapsedTime={elapsedTime}
                    recordedVideoUrl={recordedVideoUrl}
                    textStartTime={textStartTime}
                    textEndTime={textEndTime}
                    setTextStartTime={setTextStartTime}
                    setTextEndTime={setTextEndTime}
                />
            )}

            {/* Step 3 Content */}
            {step === 3 && (
                <VideoForm
                    description={description}
                    setDescription={setDescription}
                    handleDraftClick={handleDraftClick}
                    selectedCategory={selectedCategory}
                    setSelectedCategory={setSelectedCategory}
                    isPrivate={isPrivate}
                    onPrivateToggle={handlePrivateToggle}
                    allowComments={allowComments}
                    setAllowComments={setAllowComments}
                    allowDownload={allowDownload}
                    setAllowDownload={setAllowDownload}
                    onPostVideo={handlePostVideo}
                    handleSaveToStorageDraft={handleSaveToStorageDraft}
                    isUploadingToS3={isUploadingToS3}
                    isPosting={isPosting}
                    isSavingDraft={isSavingDraft}
                    uploadProgress={reduxUploadProgress}
                    uploadError={uploadError}
                    onCancelUpload={handleCancelUpload}
                    customThumbnailPreview={customThumbnailPreview}
                    thumbnailInputRef={thumbnailInputRef}
                    onRemoveThumbnail={handleRemoveThumbnail}
                />
            )}

            {/* Filter bottom sheet */}
            <AnimatePresence>
                {showFilterSheet && (
                    <>
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="absolute inset-0 z-40"
                            onClick={() => setShowFilterSheet(false)}
                        />
                        <motion.div
                            initial={{ y: "100%" }}
                            animate={{ y: 0 }}
                            exit={{ y: "100%" }}
                            transition={{ type: "spring", damping: 30, stiffness: 300 }}
                            className="absolute bottom-0 left-0 right-0 bg-white rounded-t-2xl z-50 pb-6"
                        >
                            <div className="flex justify-center pt-2 pb-3">
                                <div className="w-10 h-1 rounded-full bg-gray-300" />
                            </div>
                            <div className="flex items-center justify-between px-4 pb-4">
                                <p className="text-[11px] font-semibold tracking-widest text-gray-500 uppercase">
                                    Filters Preview
                                </p>
                                <button
                                    onClick={() => setShowFilterSheet(false)}
                                    className="text-[#007AFF] text-[15px] font-semibold"
                                >
                                    Done
                                </button>
                            </div>
                            <div className="flex gap-3 px-4 overflow-x-auto pb-1">
                                {[
                                    { label: "Clear", value: "none" },
                                    { label: "Warm", value: "brightness(1.1) saturate(1.4) sepia(0.25)" },
                                    { label: "Cold", value: "brightness(1.05) saturate(0.9) hue-rotate(195deg)" },
                                    { label: "Sepia", value: "sepia(1)" },
                                    { label: "Vintage", value: "sepia(0.4) contrast(1.1) brightness(0.9) saturate(0.85)" },
                                    { label: "B&W", value: "grayscale(1)" },
                                ].map((opt) => (
                                    <button
                                        key={opt.value}
                                        onClick={() => setSelectedFilter(opt.value)}
                                        className={`flex-shrink-0 px-5 py-2 rounded-xl text-[13px] font-semibold transition-colors ${(selectedFilter || "none") === opt.value
                                            ? "bg-[#BDE6FF] text-black"
                                            : "bg-gray-100 text-gray-800"
                                            }`}
                                    >
                                        {opt.label}
                                    </button>
                                ))}
                            </div>
                        </motion.div>
                    </>
                )}
            </AnimatePresence>

            {/* Popups */}
            <ExitPopup
                showExitPopup={showExitPopup}
                handleSaveToDraft={handleSaveToDraft}
                handleCancel={handleCancel}
                handleExitWithoutSaving={handleExitWithoutSaving}
            />

            <CompletionPopup
                showCompletionPopup={showCompletionPopup}
                handleContinue={handleContinue}
                handleSaveToStorageDraft={handleSaveToStorageDraft}
                handleDeleteVideo={handleDeleteVideo}
                isSavingDraft={isSavingDraft}
            />

            <DraftPopup
                showDraftPopup={showDraftPopup}
                handleDraftConfirm={handleDraftConfirm}
                handleDraftCancel={handleDraftCancel}
            />

            <TextDurationEditor
                isVisible={showTextDurationEditor}
                onClose={handleCloseTextDurationEditor}
                elapsedTime={elapsedTime}
                recordedVideoUrl={recordedVideoUrl}
                textStartTime={textStartTime}
                textEndTime={textEndTime}
                setTextStartTime={setTextStartTime}
                setTextEndTime={setTextEndTime}
            />
            {showFormatAlert && <div className="absolute h-full w-full top-0 justify-center items-center">
                {/* Format Alert Popup */}
                <AnimatePresence>
                    {showFormatAlert && (
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="absolute justify-center items-center h-full bg-black bg-opacity-50 flex z-50"
                            onClick={() => setShowFormatAlert(false)}
                        >
                            <motion.div
                                initial={{ opacity: 0, scale: 0.9, y: 20 }}
                                animate={{ opacity: 1, scale: 1, y: 0 }}
                                exit={{ opacity: 0, scale: 0.9, y: 20 }}
                                transition={{ duration: 0.3 }}
                                className="bg-white rounded-lg shadow-xl p-6 mx-4 max-w-sm w-full"
                                onClick={(e) => e.stopPropagation()}
                            >
                                <div className="text-center">

                                    <h3 className="text-md font-semibold text-gray-900 mb-2">
                                        Invalid Video File
                                    </h3>
                                    <p className="text-gray-600 mb-6 text-[12px]">
                                        Please select a valid video file.
                                    </p>
                                    <button
                                        onClick={() => setShowFormatAlert(false)}
                                        className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-1 px-4 rounded-lg transition-colors duration-200"
                                    >
                                        OK
                                    </button>
                                </div>
                            </motion.div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>}

            {/* Private Folder Not Created Popup */}
            {showPrivatePopup && (
                <div className="absolute inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
                    <div className="bg-white w-[80%] max-w-sm dark:bg-gray-900 px-6 py-4 rounded-xl shadow-lg text-center">
                        <div className="mb-3">
                            <div className="mx-auto w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                                <FolderLock className="w-6 h-6 text-red-600" />
                            </div>
                        </div>
                        <p className="text-[18px] font-bold mb-2 text-gray-900 dark:text-white">Private Folder Not Created</p>
                        <p className="mb-6 text-[13px] text-gray-600 dark:text-gray-300">
                            You need to create a private folder before you can upload private videos.
                        </p>
                        <div className="flex flex-col gap-3">
                            <button
                                className="flex-1 text-[14px] bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-200 font-semibold px-6 py-2 rounded-full transition-colors"
                                onClick={() => setShowPrivatePopup(false)}
                            >
                                Skip
                            </button>
                            <button
                                className="flex-1 text-[14px] bg-[#BDE6FF] hover:bg-[#9dd5f0] text-black font-semibold px-6 py-2 rounded-full transition-colors"
                                onClick={() => {
                                    setShowPrivatePopup(false);
                                    navigate("/account/private/empty");
                                }}
                            >
                                Create Now
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CreateVideos;